import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy } from '@angular/router';

/**
 * The app's single route reuse rule, provided once in AppModule.
 *
 * Shells stay, pages rebuild:
 * - A route with a child in the new tree (the root, `FeatureLayoutComponent` with the
 *   sidebar and transactions panel, lazy parents such as `dashboard`) is kept when its
 *   config is unchanged, so moving between pages no longer tears the layout down,
 *   re-fetches the panel or flickers the sidebar.
 * - The page itself (the leaf) is always rebuilt, even for the same route with new params or
 *   query params. Pages read their params once in ngOnInit and `?focus=` / filter links
 *   rely on a fresh page, which is what the old app-wide `shouldReuseRoute = () => false`
 *   (set by the dashboard and the login card, never restored) gave them.
 *
 * Screens that want to keep their page for one navigation (nft-detail's
 * `clearFocusQueryParam`) still swap `shouldReuseRoute` for that navigation and put it back.
 *
 * A page that keeps its own state in the query string (tabs, search, sort, filters) and writes
 * it to the URL after changing it in place opts in with `data: { reuseOnQueryChange: true }`:
 * it is kept when only the query string changes, so a tab click or a search does not reload
 * the whole page. A change of path or path params still rebuilds it.
 */
@Injectable({ providedIn: 'root' })
export class AppRouteReuseStrategy extends BaseRouteReuseStrategy {
    override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        if (future.routeConfig !== curr.routeConfig) return false;
        if (future.firstChild !== null) return true;
        return future.data?.['reuseOnQueryChange'] === true && samePathParams(future, curr);
    }
}

/** Whether two snapshots of one route have the same path params (`/nft/1` vs `/nft/2` differ). */
function samePathParams(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    const keys = new Set([...Object.keys(future.params), ...Object.keys(curr.params)]);
    return [...keys].every((key) => future.params[key] === curr.params[key]);
}
